import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle } from "lucide-react";
import { API, getConfig, inr, waLink } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AccountShell, { StatusPill, fmtDate } from "@/components/AccountShell";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AdminProducts from "@/components/AdminProducts";
import { PAY_LABEL } from "@/components/AccountShell";
import { AdminEmailSettings, AdminOrderEmails } from "@/components/AdminOrderEmails";
import { AdminDelivery } from "@/components/tracking/AdminDelivery";
import { useLatestRequest } from "@/hooks/useLatestRequest";
import { AdminEnquiries } from "@/components/AdminEnquiries";
import { AdminPreparation } from "@/components/AdminPreparation";
import { AdminOrderPreparation, allowedOrderStatuses } from "@/components/AdminOrderPreparation";

const PAY_STATUSES = ["pending", "verification_pending", "paid", "cod", "failed"];

const Stat = ({ label, value, testId }) => (
  <div className="min-w-0 rounded-3xl border border-cream-muted bg-cream-secondary p-4 sm:p-5" data-testid={testId}>
    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground" data-testid={`${testId}-label`}>{label}</p>
    <p className="mt-2 break-words font-display text-2xl font-semibold text-ragi-dark sm:text-3xl" data-testid={`${testId}-value`}>{value}</p>
  </div>
);

const Select = ({ value, options, onChange, testId }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className="min-w-0 max-w-full rounded-full border border-ragi/20 bg-cream px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-ragi-dark" data-testid={testId}>
    {options.map((o) => <option key={o} value={o} data-testid={`${testId}-option-${o}`}>{o}</option>)}
  </select>
);

export default function Admin() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [products, setProducts] = useState([]);
  const [wa, setWa] = useState("");
  const { run, cancel } = useLatestRequest();

  const load = useCallback(() => {
    return run((signal) => Promise.all(["summary", "orders", "enquiries", "products"].map((path) => axios.get(`${API}/admin/${path}`, { signal }))),
      ([s, o, e, p]) => { setSummary(s.data); setOrders(o.data); setEnquiries(e.data); setProducts(p.data); }, () => toast.error("Could not load dashboard"));
  }, [run]);

  useEffect(() => {
    setSummary(null); setOrders([]); setEnquiries([]); setProducts([]);
    if (!user?.is_admin) return cancel;
    let active = true;
    load();
    getConfig().then((c) => { if (active) setWa(c.whatsapp_number || ""); }).catch(() => {});
    return () => { active = false; cancel(); };
  }, [user, load, cancel]);

  const updateOrder = async (id, patch) => {
    try {
      const r = await axios.patch(`${API}/admin/orders/${id}`, patch);
      setOrders((prev) => prev.map((o) => (o.order_id === id ? r.data : o)));
      toast.success("Order updated");
      return true;
    } catch (error) { toast.error(typeof error.response?.data?.detail === "string" ? error.response.data.detail : "Update failed"); return false; }
  };

  const updateEnquiry = async (id, status) => {
    try {
      const r = await axios.patch(`${API}/admin/enquiries/${id}`, { status });
      setEnquiries((prev) => prev.map((e) => (e.id === id ? r.data : e)));
    } catch { toast.error("Update failed"); }
  };

  return (
    <AccountShell title="Owner Dashboard" eyebrow="Kandula Home Foods" requireAdmin testId="admin-page">
      {summary && (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-5" data-testid="admin-summary">
          <Stat label="Orders" value={summary.orders} testId="stat-orders" />
          <Stat label="Revenue" value={inr(summary.revenue)} testId="stat-revenue" />
          <Stat label="Awaiting confirm" value={summary.pending} testId="stat-pending" />
          <Stat label="Enquiries" value={summary.enquiries} testId="stat-enquiries" />
          <Stat label="Customers" value={summary.customers} testId="stat-customers" />
        </div>
      )}
      <Tabs defaultValue="orders" className="mt-10 min-w-0">
        <TabsList className="grid h-auto w-full grid-cols-2 gap-1 bg-cream-secondary sm:inline-flex sm:w-auto" data-testid="admin-dashboard-tabs">
          <TabsTrigger value="orders" className="min-w-0 whitespace-normal px-2 text-xs sm:px-3 sm:text-sm" data-testid="admin-tab-orders">Orders ({orders.length})</TabsTrigger>
          <TabsTrigger value="enquiries" className="min-w-0 whitespace-normal px-2 text-xs sm:px-3 sm:text-sm" data-testid="admin-tab-enquiries">Enquiries ({enquiries.length})</TabsTrigger>
          <TabsTrigger value="products" className="min-w-0 whitespace-normal px-2 text-xs sm:px-3 sm:text-sm" data-testid="admin-tab-products">Products ({products.length})</TabsTrigger>
          <TabsTrigger value="kitchen" className="min-w-0 whitespace-normal px-2 text-xs sm:px-3 sm:text-sm" data-testid="admin-tab-kitchen">Kitchen</TabsTrigger>
        </TabsList>
        <TabsContent value="products" className="mt-6"><AdminProducts products={products} setProducts={setProducts} /></TabsContent>
        <TabsContent value="orders" className="mt-6 space-y-4" data-testid="admin-orders-list">
          {user?.is_admin && <AdminEmailSettings />}
          {orders.length === 0 && <p className="text-sm text-muted-foreground">No orders yet.</p>}
          {orders.map((o) => (
            <article key={o.order_id} className="min-w-0 break-words rounded-3xl border border-cream-muted bg-cream-secondary p-5" data-testid={`admin-order-${o.order_number}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 max-w-full">
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-terracotta">{o.order_number} <span className="ml-2 text-muted-foreground">{fmtDate(o.created_at)}</span></p>
                  <p className="mt-1 text-sm font-semibold">{o.customer.name} · <a href={`tel:${o.customer.phone}`} className="text-ragi underline-offset-2 hover:underline">{o.customer.phone}</a></p>
                  {o.customer.address && <p className="text-xs text-muted-foreground">{o.customer.address}</p>}
                  {o.customer.note && <p className="mt-1 text-xs italic text-muted-foreground">"{o.customer.note}"</p>}
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground" data-testid={`admin-order-payment-${o.order_number}`}>
                    {PAY_LABEL[o.payment_status] || o.payment_status}{o.payment_method && ` · ${o.payment_method}`}{o.upi_reference && ` · UTR ${o.upi_reference}`}{o.razorpay?.payment_id && ` · ${o.razorpay.payment_id}`}
                  </p>
                  {o.payment_status === "verification_pending" && (
                    <button onClick={() => updateOrder(o.order_id, { status: o.status, payment_status: "paid" })} className="mt-2 rounded-full bg-ragi px-3 py-1.5 text-xs font-semibold text-cream" data-testid={`admin-order-verify-${o.order_number}`}>Mark UPI payment verified</button>
                  )}
                </div>
                <div className="flex w-full min-w-0 flex-wrap items-center gap-2 sm:w-auto">
                  <StatusPill status={o.status} testId={`admin-order-status-${o.order_number}`} />
                  <Select value={o.status} options={allowedOrderStatuses(o)} onChange={(v) => updateOrder(o.order_id, { status: v })} testId={`admin-order-status-select-${o.order_number}`} />
                  <Select value={o.payment_status} options={PAY_STATUSES} onChange={(v) => updateOrder(o.order_id, { status: o.status, payment_status: v })} testId={`admin-order-payment-select-${o.order_number}`} />
                  {wa && <a href={waLink(o.customer.phone.replace(/\D/g, "").replace(/^(\d{10})$/, "91$1"), `Hi ${o.customer.name}, this is Kandula Home Foods about your order ${o.order_number}.`)} target="_blank" rel="noopener noreferrer" className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#25D366] text-white" aria-label="WhatsApp customer" data-testid={`admin-order-wa-${o.order_number}`}><MessageCircle className="h-4 w-4" /></a>}
                </div>
              </div>
              <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-1 border-t border-cream-muted pt-3 text-xs">
                {o.items.map((i) => <li key={i.product_id} className="max-w-full break-words" data-testid={`admin-order-item-${o.order_id}-${i.product_id}`}>{i.name} <span className="text-muted-foreground">{i.unit} ×{i.quantity}</span></li>)}
                <li className="ml-auto font-display text-base font-bold text-terracotta">{inr(o.total)}</li>
              </ul>
              <AdminOrderPreparation order={o} onUpdate={updateOrder} />
              <AdminOrderEmails order={o} onUpdated={(email_status) => setOrders((prev) => prev.map((entry) => entry.order_id === o.order_id ? { ...entry, email_status } : entry))} />
              <AdminDelivery order={o} />
            </article>
          ))}
        </TabsContent>
        <TabsContent value="enquiries" className="mt-6"><AdminEnquiries key={user?.user_id} enquiries={enquiries} setEnquiries={setEnquiries} onStatusChange={updateEnquiry} /></TabsContent>
        <TabsContent value="kitchen" className="mt-6"><AdminPreparation key={user?.user_id} /></TabsContent>
      </Tabs>
    </AccountShell>
  );
}
